import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useColetaPrecosListaViewModel } from "../../viewModels/coleta_precos_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateColetaPrecosListaColumnDefinition } from "./columns";
import { ColetaPrecoLista } from "../../../domain/models/coleta_preco_lista";
import { LegendaModal } from "./components/legenda_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * ColetaPrecosLista
 */

export const ColetaPrecosLista = () => {
  const viewModel = useColetaPrecosListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateColetaPrecosListaColumnDefinition({
      onActionCopiar: viewModel.handleColetaPrecoCopy,
      onActionEditar: viewModel.handleColetaPreco,
      onActionDeletar: viewModel.handleColetaPrecoModalOpenState,
      onActionExportar: viewModel.handleColetaPrecoExportar,
      onActionOrdemCompra: viewModel.handleColetaPrecoOrdemCompra,
    });
  }, [
    viewModel.handleColetaPrecoCopy,
    viewModel.handleColetaPreco,
    viewModel.handleColetaPrecoModalOpenState,
    viewModel.handleColetaPrecoExportar,
    viewModel.handleColetaPrecoOrdemCompra,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleColetaPreco(null, true);
                    } else {
                      viewModel.handleColetaPreco(null, e.ctrlKey);
                    }
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Compras
          </Link>
          <Typography className="text-primary-green">
            Coleta de Preços
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Coleta de Preços</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex gap-2">
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload}
                />
              </div>
            ) : (
              <>
                <Header
                  dataRequisicaoStart={viewModel.filters.dataRequisicaoStart}
                  dataRequisicaoEnd={viewModel.filters.dataRequisicaoEnd}
                  fornecedor={viewModel.filters.fornecedor}
                  requisicao={viewModel.filters.requisicao}
                  produtoServico={viewModel.filters.produtoServico}
                  centroCusto={viewModel.filters.centroCusto}
                  nroColeta={viewModel.filters.nroColeta}
                  ordemCompra={viewModel.filters.ordemCompra}
                  status={viewModel.filters.status}
                  fornecedoresDropdown={viewModel.fornecedoresDropdown}
                  requisicoesCompraDropdown={viewModel.requisicoesCompraDropdown}
                  produtoServicosDropdown={viewModel.produtoServicosDropdown}
                  centroCustoDropdown={viewModel.centroCustoDropdown}
                  ordemComprasDropdown={viewModel.ordemComprasDropdown}
                  onDataRequisicaoChange={viewModel.handleDataRequisicaoChange}
                  onFornecedorChange={viewModel.handleFornecedorChange}
                  onRequisicaoChange={viewModel.handleRequisicaoChange}
                  onProdutoServicoChange={viewModel.handleProdutoServicoChange}
                  onCentroCustoChange={viewModel.handleCentroCustoChange}
                  onNroColetaChange={viewModel.handleNroColetaChange}
                  onOrdemCompraChange={viewModel.handleOrdemCompraChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col justify-end my-2">
                      <span
                        className="text-[#009C4E] bg-transparent border border-[#009C4E] px-2 py-1 text-sm rounded rounded-base font-semibold cursor-pointer"
                        onClick={() => viewModel.handleShowLegenda(true)}>
                        Legenda
                      </span>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="coleta_precos_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    textSize="pequeno"
                    getRowId={(row: ColetaPrecoLista) => row.idColetaPreco}
                    getRowHeight={() => 'auto'}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.coletaPrecos}
                    rowCount={viewModel.coletaPrecos.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <LegendaModal
        open={viewModel.legendalModal}
        onClose={() => viewModel.handleShowLegenda(false)}
      />

      <DeleteDialog
        open={viewModel.coletaPrecoDeleteModal}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleColetaPrecoModalOpenState(null, false)}
        onDelete={() => viewModel.handleColetaPrecoDeleteSubmit()}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

    </>
  );
}